import z from 'zod'
import { HowDidYouHearType, StockRules } from '../firebase/types'
import { SchoolType_v2 } from '../firebase/types-school'
import {
    ECCAccreditationLevel,
    EducatorProfile,
    Languages,
    NSWAccreditationLevel,
    SupportStaffProfile,
    TeacherAccountState,
    TeacherProfile,
} from '../firebase/types-teacher'
import { fieldStates, WorkingRightsType } from '../schemas/teacherReg'
import { isDocExpired } from '../helpers/utils'

// export const accountTypeFilter = z.enum([AccountType])
export const accountTypes = {
    teacher: 'teacher',
    school: 'school',
    childcareCentre: 'childcareCentre',
    organisation: 'organisation',
} as const
export type AccountType = (typeof accountTypes)[keyof typeof accountTypes]
// export type AccountType = 'teacher' | 'school' | 'childcareCentre' | 'organisation'
export const teacherEmploymentSetting = z.enum([
    'school',
    'earlyChildcare',
    'supportStaff',
] as const)
export type TeacherEmploymentSetting = z.infer<typeof teacherEmploymentSetting>

export const TeacherEmploymentSettings = [
    'school',
    'earlyChildcare',
    'supportStaff',
] as const

export type LocalTeacherEmploymentType =
    | 'school'
    | 'earlyChildcare'
    | 'both'
    | 'supportStaff'
export type LocalTeacherEmploymentSetting = {
    id?: string
    locationState: string
    type: LocalTeacherEmploymentType
    state?: TeacherAccountState
    level?: ECCAccreditationLevel

    hasQualification?: boolean
}

export type UserType = {
    firstName?: string
    firstNameActual?: string
    lastName?: string
    email?: string
    accountType?: AccountType | null
    dateRegistered?: string
    contact?: string
    homeCountry?: string
    homeState?: string
    lastVerified?: number
    profilePictureURL?: string
    registrationDevice?: string

    schoolName?: string
    schoolUidWeb?: string // Uid state for web
    schoolUid?: string
    schools?: {
        [schoolId: string]: UserInstitution
    }
    centreName?: string
    centreUid?: string
    centres?: {
        [centreId: string]: UserInstitution
    }
}

export type UserInstitution = {
    name: string
    suburb: string
    logoURL?: string
    type: 'school' | 'childcareCentre'
}

export type User_School = UserType & {
    schoolName?: string
    schoolUid?: string
    schools?: {
        [schoolId: string]: {
            name: string
            suburb: string
        }
    }
}

export type User_Centre = UserType & {
    centreName?: string
    centreUid?: string
    centres?: {
        [centreId: string]: {
            name: string
            suburb: string
        }
    }
}

export type ShopCart = {
    fbId: string
    stripeId: string
    name: string
    color: string
    size: string
    singleUnitPrice: number
    previewImg: string
    orderCount: number
    date: number
    stockRule: undefined | StockRules
}

export type OtherDocument = {
    type?: string | null
    submitted?: string | null
    value?: string | null
    state?: string | null
    docTitle?: string | null
}
export const toOtherDocument = (data: any): OtherDocument => {
    return {
        type: data.type,
        submitted: data.submitted,
        value: data.value,
        docTitle: data.docTitle,
    }
}

export interface BankDetails {
    id?: string
    accountName?: string
    accountNumber?: string
    bsb?: string
    timeSubmitted?: number
}

export type IdentificationDocument = {
    id?: string
    type?: string
    documentType?: string
    country?: string
    name?: string
    birthDate?: string
    reference?: string
    value?: string
    state?: string
    docTitle?: string
}
export const toIdentificationDocument = (
    data: any,
    id: string
): IdentificationDocument => {
    return {
        id: id,
        type: data.type,
        documentType: data.documentType,
        country: data.country,
        name: data.name,
        birthDate: data.birthDate,
        reference: data.reference,
        value: data.value,
        docTitle: data.docTitle,
    }
}

export type AccreditationRequiredField =
    | 'expiryDate'
    | 'registrationDate'
    | null
export type AccreditationDocumentDetails = {
    id?: string
    type?: string
    short_title?: string
    long_title?: string
    frequency?: string
    requirement_description?: string
    website_link?: string
    details_description?: string

    upload_instructions?: string
    required_datefield?: AccreditationRequiredField
    optional?: boolean
    renewal_frequency_years?: number
    no_reference?: boolean
}

const mimeType = z.string().describe('MIME Type')

export const qualificationTypes = new Set([
    'supportStaffQualification',
    'earlyChildcareQualification',
])
export const accreditationDocumentSchema = z
    .object({
        expiryDate: z.string().nullable().default(null),
        registrationDate: z.string().nullable().default(null),
        dateSubmitted: z.string().nullable().default(null),
        imageURL: z.url().nullable().default(null),
        reference: z.string().nullable().default(null),

        type: mimeType.default(''),
        state: fieldStates,
        docTitle: z.string().nullable().default(null),
        accreditationType: z.string(),
        qualification: z.string().nullable().default(null),
        classification: z.string().nullable().default(null),
        courseType: z.string().nullable().default(null),
    })
    .transform((v) => ({
        ...v,
        state: isDocExpired(v.expiryDate || undefined) ? 'expired' : v.state,
    }))
// .check((ctx) => {
//     if (
//         qualificationTypes.has(ctx.value.accreditationType) &&
//         (!ctx.value.qualification)
//     ) {
//         ctx.issues.push({
//             code: 'custom',
//             path: ['qualification'],
//             message:
//                 'Qualification is required for this accreditation type',
//             input: ctx.value.qualification,
//         })
//     }
// })
export type AccreditationDocument_v2 = z.infer<
    typeof accreditationDocumentSchema
>
export const accreditationDocumentsSchema = z.record(
    z.string(),
    accreditationDocumentSchema
)
export type AccreditationDocuments = z.infer<
    typeof accreditationDocumentsSchema
>

export type AccreditationDocument = {
    id?: string
    accreditationType?: string | null
    expiryDate?: string
    dateSubmitted?: string
    imageURL?: string | null
    reference?: string
    type?: string | null
    state?: string

    docTitle?: string | null
    qualification?: string | null
    registrationDate?: string
}
export const toAccreditationDocument = (
    data: any,
    id: string
): AccreditationDocument => {
    return {
        id: id,
        accreditationType: data.accreditationType,
        expiryDate: data.expiryDate,
        dateSubmitted: data.dateSubmitted,
        imageURL: data.imageURL,
        reference: data.reference,
        type: data.type,
        state: data.state,
        docTitle: data.docTitle,
        qualification: data.qualification,
        registrationDate: data.registrationDate,
        // userSkipped: data.userSkipped
    }
}

export type StateInfo = {
    short_name: string
    long_name: string
    docs: string[]
}
export type RegAccredDocs = { [name: string]: AccreditationDocument } & {
    state?: string
}
export type TeacherFields = {
    accountState?: {
        dateApproved?: string
        lastUpdated?: string
        value?: TeacherAccountState
        firstTime?: boolean
        applyDate?: string
    }
    firstName?: { value?: string; state?: string }
    lastName?: { value?: string; state?: string }
    firstNameActual?: { value?: string; state?: string }
    preferredName?: { value?: string; state?: string }
    email?: { value?: string; state?: string }
    contact?: { value?: string; state?: string }
    homeAddress?: {
        address?: string
        country?: string
        homeState?: string
        lat?: number
        long?: number
        postCode?: string
        suburb?: string
        state?: string
    }
    profilePicture?: {
        value?: string
        quality?: string
        type?: string
        state?: string
    }
    employmentSetting?: EmploymentSettings
    favouriteSchools?: Record<string, SchoolType_v2>

    type?: {
        value: SchoolType_v2
    }

    // Account Registration Details
    workingRights?: {
        expiry?: string
        reference?: string
        value?: WorkingRightsType
        state?: string
        url?: string
        docTitle?: string
        type?: string

        // New Temp Visa Fields
        grant?: string
        visaClass?: string
        workingEntitlements?: boolean
    }
    primaryIdentification?: IdentificationDocument
    secondaryIdentification?: IdentificationDocument
    holdingIdPhotoURL?: IdentificationDocument
    accreditation?: {
        // employmentType is of type TeacherEmploymentSetting ('school' | 'earlyChildcare')
        [employmentType: string]: {
            [locationState: string]: RegAccredDocs | null
        } | null
    }
    setLocation?: {
        country?: string
        homeState?: string
        lat?: number
        long?: number
    }
    firstEmergencyContact?: {
        contact?: string
        name?: string
        relationship?: string
        state?: string
    }
    secondEmergencyContact?: {
        contact?: string
        name?: string
        relationship?: string
        state?: string
    }
    reference?: {
        firstName?: string
        lastName?: string
        email?: string
        state?: string
        requestedInterview?: boolean

        whenWeLastWorkedTogether?: string
        whereWeWorkedTogether?: string
        workingRelationship?: string
        workplaceUid?: string
    }

    // Pre-employment Documents
    bank?: OtherDocument & BankDetails
    super?: {
        current?: {
            docUrl: string
        }
        history?: Record<number, string>
        state?: string
    }

    tax?: {
        current?: {
            docUrl: string
        }
        history?: Record<number, string>
        state?: string
    }

    // Accreditation Level (Only for NSW School Teachers)
    service?: {
        serviceId?: string
        timeUploaded?: number
        uploadedBy?: string
        urls?: {
            [docId: string]:
                | {
                      value: string
                      type?: string
                      docTitle?: string
                  }
                | string
        }
        userLevelNomination?: NSWAccreditationLevel
        userLocation?: string
    }

    // Profile Fields
    profile?: Partial<TeacherProfile>
    supportStaffProfile?: Partial<SupportStaffProfile>
    educatorProfile?: Partial<EducatorProfile>
    languages?: Languages

    // Other stats
    completionRate?: {
        school?: number
        childcare?: number
        supportStaff?: number
    }
    completedSchools?: Record<string, boolean>

    // Additional account fields
    employmentDeclarations?: any
    firstTime?: string
    uiState?: { showProfileBanner?: boolean }
    howDidYouHear?: HowDidYouHearType
}

export type EmploymentSetting = {
    locationState: string
    type: TeacherEmploymentSetting
    state?: TeacherAccountState
    level?: ECCAccreditationLevel
    hasQualification?: boolean
}
export type EmploymentSettings = Record<string, EmploymentSetting>

export const getTeacherFields = (data: any): TeacherFields => {
    return {
        ...(data.firstNameActual
            ? { firstName: data.firstNameActual }
            : undefined),
        ...(data.firstName ? { preferredName: data.firstName } : undefined),
        ...(data.lastName ? { lastName: data.lastName } : undefined),
        ...(data.email ? { email: data.email } : undefined),
        ...(data.contact ? { contact: data.contact } : undefined),
        ...(data.homeLocation ? { homeAddress: data.homeLocation } : undefined),
        ...(data.profilePicture
            ? { profilePicture: data.profilePicture }
            : undefined),
        ...(data.identity ? { workingRights: data.profilePicture } : undefined),
        ...(data.employmentDeclarations
            ? { employmentDeclarations: data.employmentDeclarations }
            : undefined),
    }
}

// export type TeacherProfile = {
//     profile: { about: string } | null
//     favouriteSubjects: { [subject: string]: boolean } | null
//     years: { max: number; min: number } | null
//     type: { value: 'Primary' | 'Secondary' | 'Primary & Secondary' } | null
// }

// export type SupportStaffProfile = {
//     profile: { about: string; supportsAdditionalNeeds?: boolean } | null
//     years: { max: number; min: number } | null
//     supportStaffSettings: {
//         primary?: boolean
//         secondary?: boolean
//     } | null
// }

export type TeacherNotifications = {
    bookingConfirmations?: boolean
    cancellationNotifications?: boolean
    completedBookings?: boolean
    onboardingEmails?: boolean
    sparkNews?: boolean
    teacherUpdates?: boolean
    accountReminderEmails?: boolean
}

export enum REG_STEP {
    NAMES = 'names',
    CONTACT = 'contact',
    PROFILE_PHOTO = 'profilePhoto',
    WORKING_RIGHTS = 'workingRights',
    PRIMARY_DOC = 'primaryIdentity',
    SECONDARY_DOC = 'secondaryIdentity',
    HOLDING_ID = 'photoHoldingID',
    TEACHING_DOCS = 'teachingDocuments',
    REFERENCE = 'reference',
    EMERGENCY_CONTACTS = 'emergency',
    BANK = 'bank',
    SUPER = 'super',
    TAX = 'tax',
    REVIEW = 'review',

    ACCREDITATION_LEVEL = 'accreditationLevel',
    WORKING_HISTORY = 'workingHistory',
}

export enum SCHOOL_REG_STEP {
    BASIC_DETAILS = 'basicDetails',
    SCHOOL_INFORMATION = 'schoolInformation',
    PRIMARY_BELLTIMES = 'primaryBellTimes',
    IS_PRIMARY_SECONDARY = 'isPrimarySecondary',
    SECONDARY_BELLTIMES = 'secondaryBellTimes',
    REVIEW_BELLTIMES = 'reviewBellTimes',
    BM_DETAILS = 'businessDetails',
    TERMS = 'terms',
    SUBMIT = 'submit',
}
